<template lang="pug">
v-dialog(
  :value="shown"
  max-width="500"
  persistent
)
  v-card

    v-toolbar-title
      v-card-title
        | {{ title }}
      v-card-subtitle.x-text-wrap(v-if="description")
        | {{ description }}

    //- Pendindo confirmação...
    //-
    template(v-if="!accepted && action")
      v-card-text.font-wight-medium
        v-row
          v-col(cols="2")
            v-icon.pa-2(large) mdi-help-circle
          v-col(auto)
            span Você está prestes a realizar a operação:
            br
            span.pl-4 {{ title }}
            br
            span Deseja realmente confirmar?
      v-card-actions
        v-spacer
        v-btn(
          color="primary"
          @click="accept()"
        )
          | Confirmar
        v-btn(@click="close(false)")
          | Cancelar


    //- Mal sucedido...
    //-
    template(v-else-if="error")
      v-card-text.font-wight-medium
        div
          p.font-weight-bold(:class="errorClass")

            v-icon(
              v-if="error.code >= 500"
              color="error"
              left
            )
              | mdi-close-octagon

            v-icon(
              v-else
              color="warning"
              left
            )
              | mdi-alert-octagon

            | Hmmm... Algo não deu certo!

        p.ma-0.pa-0.font-weight-regular(:class="errorClass", v-html="error.message")

        v-progress-linear.mb-3(
          value="100"
          :color="error.code >= 500 ? 'error' : 'warning'"
          rounded
        )

        p.ma-0.pa-0.font-weight-light(
          v-for="(detail, index) in error.details"
          :key="`detail-${index}`"
        )
          | {{ detail }}

      v-card-actions
        v-spacer
        v-btn(@click="close(false)")
          | Fechar


    //- Bem sucedido...
    //-
    template(v-else-if="ok")
      v-card-text.font-wight-medium
        div
          p.font-weight-bold.success--text
            v-icon(color="success" left) mdi-check-circle
            | Concluído!

        p.ma-0.pa-0.font-weight-regular.success--text {{ ok.message }}

        v-progress-linear.mb-3(
          value="100"
          color="success"
          rounded
        )

        p.ma-0.pa-0.font-weight-light(
          v-for="(detail, index) in ok.details"
          :key="`detail-${index}`"
        )
          | {{ detail }}

      v-card-actions
        v-spacer

        template
          template(v-if="autoClose.enabled && !autoClose.pending")
            v-btn(
              @click="close(true)"
              text
            )
              | Fechando

          template(v-else)
            v-btn(@click="close(true)") Fechar

        template
          template(v-if="autoClose.enabled")
            v-progress-circular.mr-1.ml-2.x-autoclose-progress(
              v-if="autoClose.pending"
              :value="autoClose.percent"
              :rotate="-90"
              :size="18"
              :width="2"
              color="success"
            )
            v-icon.mr-1.ml-2(
              v-else
              color="success"
              right
            )
              | mdi-check-circle

    //- Executando...
    //-
    template(v-else)
      v-card-text.font-wight-medium
        p {{ gerund }}...
        v-progress-linear(
          value="0"
          indeterminate
          rounded
        )
      v-card-actions
        v-spacer
        v-btn(
          :disabled="!cancelable"
          @click="close(false)"
        )
</template>

<style>
.x-text-wrap {
  white-space: normal;
}
.x-autoclose-progress * {
  transition: none !important
}
</style>

<script>
import { createStatus } from '@/helpers/entities'
import moment from 'moment'

export default {

  props: {
    site: String,
    eref: String,
    form: Object,
    data: Object,
    entity: Object,

    action: Object, // {Keep.Paper.Design.Link}
  },

  data: () => ({
    abort: null,
    accepted: false,
    status: null, // {Keep.Paper.Design.Status}

    autoClose: {
      enabled: false,
      pending: true,
      elapsed: 0,
      percent: 0,
    },
  }),

  computed: {
    shown: vm => !!vm.action,

    title: vm => vm.action?.title || vm.action?.verb || vm.action?.name,
    gerund: vm => vm.action?.gerund || `Realizando a operação: ${vm.title}`,
    description: vm => vm.action?.description,
    cancelable: vm => vm.action?.cancelable,

    ok () {
      if (!this.status?.ok)
        return null

      return {
        code: this.status.code || 200,
        message: this.status.message,
        details: this.status.details || []
      }
    },
    error () {
      if (!this.status || this.status.ok)
        return null

      const message = (this.status.message || this.status.entity?.message)?.replace(/\n/g, '<br>');

      return {
        code: this.status.code,
        message: message || 'Houve uma falha não identificada durante a execução do procedimento.',
        details: this.status.details || []
      }
    },
    errorClass() {
      return this.error.code >= 500 ? 'error--text' : 'warning--text'
    },

    safe: vm => vm.action?.rule?.some(rule => rule === 'Safe'),
    review: vm => vm.action?.rule?.some(rule => rule === 'Review'),
  },

  methods: {
    async accept () {
      try {
        this.autoClose.enabled = false
        this.autoClose.pending = true

        this.accepted = true

        this.status = await this.$resolve(this.action)

        // Se a operação é considerada segura e foi bem sucedida podemos
        // fechá-la automaticamente...
        if (this.status.ok && !this.review) {
          // this.$nextTick(function () { this.close(true) })
          this.$nextTick(function () { this.delayAndClose() })
        }

      } catch (error) {
        this.status = createStatus(this, error, this.action)
      } finally {
        this.$emit('actionComplete', {
          success: this.status.ok,
          status: this.status,
          action: this.action
        })
      }
    },

    delayAndClose(startTime) {
      const self = this
      const { autoClose } = this
      const maximum = this.autoClose.pending ? 600 : 300;

      startTime ||= moment()

      autoClose.enabled ||= true
      autoClose.elapsed = moment() - startTime
      autoClose.percent = (autoClose.elapsed / maximum) * 100

      if (autoClose.elapsed < maximum) {
        setTimeout(function () { self.delayAndClose(startTime) }, 20);
      } else if (this.autoClose.pending) {
        this.autoClose.pending = false
        this.$nextTick(function () { self.delayAndClose() });
      } else {
        this.$nextTick(function () { this.close(true) })
      }
    },

    activate () {
      // Se a operação é considerada segura podemos executá-la sem confirmação...
      if (this.shown && this.safe) {
        this.$nextTick(this.accept)
      }
    },

    reset () {
      this.status = null
      this.accepted = false
    },

    close(success) {
      if (success && this.entity) {
        const rules = this.action?.rule || []
        const close = rules.some(x => x === 'Close')
        if (close) {
          this.$close(this.entity)
        }
      }
      this.$emit('actionTerminate', { success, status: this.status })
    },
  },

  mounted () {
    this.reset()
  },

  watch: {
    'action': 'reset',
    'shown': 'activate',
  },
}
</script>